import type { ReactNode } from "react";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { type LogLevel, LogControllerApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "../factory";

const reportPlatformErrorConfig = {
	uid: null as null | string,
};

export function configReportPlatformError(config: Partial<typeof reportPlatformErrorConfig>): void {
	Object.assign(reportPlatformErrorConfig, config);
}

const mappedLogSeverity: Record<LogLevel, (message?: any, ...optionalParams: any[]) => void> = {
	DEBUG: console.debug,
	ERROR: console.error,
	INFO: console.log,
	WARN: console.warn,
};

export type PlatformErrorAreas =
	| "user"
	| "dashboard"
	| "outlook"
	| "outlook-focus"
	| "hmm"
	| "custom-report"
	| "proxy"
	| "global"
	| "advanced-settings"
	| "portfolio"
	| "universe"
	| "market-views"
	| "market-views-settings"
	| "target-portfolio"
	| "benchmark"
	| "alerts"
	| "ask-intelligence"
	| "commentary-builder"
	| "report-builder"
	| "portfolio-creation"
	| "instrument-customisation"
	| "workspace";

export type AttemptedOperation =
	| {
			message: string;
			payload?: string;
	  }
	| string;

export async function reportPlatformErrorPromise(
	error: unknown,
	severity: LogLevel,
	area?: PlatformErrorAreas,
	attemptedOperation?: AttemptedOperation,
): Promise<void> {
	if (attemptedOperation) {
		let message = "";
		if (typeof attemptedOperation === "string") {
			message = `An error occurred while performing the following operation: ${attemptedOperation}, trace: (${error})`;
		} else {
			message = `An error occurred while performing the following operation: ${attemptedOperation.message}, ${
				attemptedOperation.payload ? `payload: ${attemptedOperation.payload}, ` : ""
			} trace: (${error})`;
		}

		mappedLogSeverity[severity](message, error);
		await getApiGen(LogControllerApiFactory).log({ level: severity, area, message });
	} else {
		const message = `An error occurred (user: "${reportPlatformErrorConfig.uid}"), trace: (${error})`;
		mappedLogSeverity[severity](message, error);
		await getApiGen(LogControllerApiFactory).log({ level: severity, area, message });
	}
}

/**
 *
 * @param error
 * @param severity
 * @param area
 * @param attemptedOperation
 */
export function reportPlatformError(
	error: unknown,
	severity: LogLevel,
	area?: PlatformErrorAreas,
	attemptedOperation?: AttemptedOperation,
): void {
	unpromisify(() => reportPlatformErrorPromise(error, severity, area, attemptedOperation).catch(noop))();
}

const TOAST_MAX_LISTED_LINES = 5;
const LIST_TOAST_AUTO_CLOSE = 15000;

type ListToastContentProps = { message: string };

/** The message's lines in order: each run of "- " lines becomes a list of at most five, the rest stay text. */
function ListToastContent({ message }: ListToastContentProps): React.JSX.Element {
	// A line's text and how many times it has appeared so far, so repeated lines keep distinct keys.
	const seen = new Map<string, number>();
	const keyOf = (text: string) => {
		const count = (seen.get(text) ?? 0) + 1;
		seen.set(text, count);
		return `${text}#${count}`;
	};
	const blocks: ReactNode[] = [];
	let run: string[] = [];
	const closeRun = () => {
		if (run.length === 0) {
			return;
		}
		const items = run;
		run = [];
		blocks.push(
			<ul key={keyOf(`list:${items[0]}`)} className="list-disc pl-4">
				{items.slice(0, TOAST_MAX_LISTED_LINES).map((item) => (
					<li key={keyOf(item)}>{item}</li>
				))}
				{items.length > TOAST_MAX_LISTED_LINES && <li>and {items.length - TOAST_MAX_LISTED_LINES} more</li>}
			</ul>,
		);
	};
	for (const line of message.split("\n")) {
		if (line.startsWith("- ")) {
			run.push(line.slice(2));
			continue;
		}
		closeRun();
		blocks.push(line.trim() ? <div key={keyOf(line)}>{line}</div> : <div key={keyOf("")} className="h-2" />);
	}
	closeRun();
	return <ToastContent>{blocks}</ToastContent>;
}

/**
 * Executes a given asynchronous function with error reporting and user notifications.
 *
 * @template T The return type of the asynchronous function.
 * @param {() => Promise<T>} fn - The asynchronous function to execute.
 * @param {Object} opts - Options for error reporting and user notifications.
 * @param {PlatformErrorAreas} opts.area - The platform area where the error occurred.
 * @param {AttemptedOperation} opts.attemptedOperation - A description of the operation attempted when the error occurred.
 * @returns {Promise<T>} A promise that resolves to the result of the asynchronous function, or rejects with the encountered error.
 *
 * @throws Will rethrow any error encountered during execution of the `fn` function after reporting it.
 *
 * @example
 * Example:
 * ```
 * <AsyncButton
 * 	onClickAsync={async () => {
 * 		const x = await runWithErrorReporting(
 * 			async () => {
 * 				try {
 * 					const response = await api();
 * 					return response;
 * 				} catch (err) {
 * 					if (err.statusCode == 502) {
 * 						throw new ToastableError("Oh nooooo", {
 * 							cause: err,
 * 							icon: "Portfolio",
 * 						});
 * 					}
 * 					throw err;
 * 				}
 * 			},
 * 			{
 * 				area: "portfolio",
 * 				attemptedOperation: "save proposal",
 * 			},
 * 		);
 * 	}}
 * >
 * 	Test
 * </AsyncButton>;
 * ```
 */

export async function runWithErrorReporting<T>(
	fn: () => Promise<T>,
	opts: {
		area: PlatformErrorAreas;
		attemptedOperation: AttemptedOperation;
	},
): Promise<T> {
	try {
		return await fn();
	} catch (err) {
		if (err instanceof ToastableError && err.listLines) {
			platformToast({
				children: <ListToastContent message={err.endUserMsg} />,
				icon: err.icon ?? "Icon-full-alert",
				severity: "error",
				autoClose: LIST_TOAST_AUTO_CLOSE,
			});
		} else if (err instanceof ToastableError) {
			platformToast({
				children: err.endUserMsg,
				icon: err.icon ?? "Icon-full-alert",
				severity: "error",
			});
		} else {
			platformToast({
				children: "Something went wrong",
				icon: "Icon-full-alert",
				severity: "error",
			});
		}

		reportPlatformError(err, "ERROR", opts.area, opts.attemptedOperation);
		throw err;
	}
}
